import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	StyledArrow,
	StyledDropdown,
	StyledOption,
	StyledOptions,
	StyledSelectedValue,
} from './Dropdown.styled';

export interface IDropdownOption {
	name: string;
	value: unknown;
}

export interface IDropdownProps {
	id: string;
	options: IDropdownOption[];
	onChange?: (option: IDropdownOption) => void;
}

const Dropdown: React.FC<IDropdownProps> = ({ id, options, onChange }) => {
	const [isOpen, setIsOpen] = useState(false);
	const [selectedOptionIndex, setSelectedOptionIndex] = useState(0);
	const dropdownRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const handleOptionClick = useCallback(
		(optionIndex: number) => {
			setSelectedOptionIndex(optionIndex);
			setIsOpen(false);

			const selectedOption = options[optionIndex];
			onChange?.(selectedOption);
		},
		[onChange, options],
	);

	const selectedOptionText = useMemo(
		() => options[selectedOptionIndex].name,
		[options, selectedOptionIndex],
	);

	return (
		<StyledDropdown ref={dropdownRef}>
			<StyledSelectedValue onClick={() => setIsOpen(!isOpen)}>
				{selectedOptionText}
				<StyledArrow $direction={isOpen ? 'up' : 'down'} />
			</StyledSelectedValue>
			{isOpen && (
				<StyledOptions>
					{options.map((option, index) => (
						<StyledOption
							key={`option#${id}#${index}`}
							onClick={() => handleOptionClick(index)}
							$isSelected={selectedOptionIndex === index}
						>
							{option.name}
						</StyledOption>
					))}
				</StyledOptions>
			)}
		</StyledDropdown>
	);
};

export { Dropdown };
